import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import PayThroughAccountModal from '../Patients/PayThroughAccountModal';
import SplitPaymentModal from '../Patients/SplitPaymentModal';
import {
  branchFromProcedureLocation,
  formatAgreedAmount,
} from './procedureBilling';
import '../../styles/BillSurgeryModal.css';

const ADMISSION_PRIVATE_CUSTOMER_ID = '4691741000000115035';

export function AdmissionInvoicePrompt({ admission, onMerge, onNew, onClose, disabled }) {
  const invoiceLabel = admission?.sales_number
    ? `admission invoice ${admission.sales_number}`
    : 'the admission invoice';

  return (
    <div className="modal-overlay bill-surgery-overlay">
      <div className="bill-surgery-modal" role="dialog" aria-labelledby="admission-invoice-title">
        <div className="modal-header">
          <h2 id="admission-invoice-title">Admission invoice found</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <p>
            This patient has {invoiceLabel}. Merge the surgery charges into that invoice, or create a new invoice?
          </p>
          <div className="bill-surgery-actions">
            <button type="button" className="primary-btn" disabled={disabled} onClick={onMerge}>
              Merge with admission invoice
            </button>
            <button type="button" className="secondary-btn" disabled={disabled} onClick={onNew}>
              Create a new invoice
            </button>
            <button type="button" className="cancel-btn" disabled={disabled} onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const BillSurgeryModal = ({ procedure, onClose, onBilled }) => {
  const { user } = useAuth();
  const procedureId = procedure?.procedure_id;
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [context, setContext] = useState(null);
  const [invoiceChoice, setInvoiceChoice] = useState(null);
  const [branch, setBranch] = useState('');
  const [showPayThroughModal, setShowPayThroughModal] = useState(false);
  const [showSplitPaymentModal, setShowSplitPaymentModal] = useState(false);
  const [registerClosed, setRegisterClosed] = useState(false);

  useEffect(() => {
    if (!procedureId) {
      setLoading(false);
      setError('Surgery not found.');
      return;
    }

    const loadContext = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/procedures/billing-context`,
          { params: { procedure_ids: procedureId } }
        );
        const data = response.data;
        setContext(data);
        setBranch(branchFromProcedureLocation(data.procedures?.[0]?.location, data.admission?.branch));
        if (!data.admission) {
          setInvoiceChoice('new');
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load surgery billing details.');
      } finally {
        setLoading(false);
      }
    };

    loadContext();
  }, [procedureId]);

  useEffect(() => {
    if (!branch) {
      setRegisterClosed(false);
      return;
    }

    const fetchRegisterStatus = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/cash-register/status`,
          { params: { branch } }
        );
        setRegisterClosed(!response.data.isOpen);
      } catch (err) {
        console.error('Error fetching cash register status:', err);
        setRegisterClosed(false);
      }
    };

    fetchRegisterStatus();
  }, [branch]);

  const patient = context?.patient;
  const admission = context?.admission;
  const mergeSelected = invoiceChoice === 'merge';
  const isPrivateWalkIn =
    patient?.patient_type === 'Private' &&
    invoiceChoice === 'new' &&
    !admission;

  const submitBill = async ({ payThroughAccount = null, payments = null } = {}) => {
    if (!context?.has_prices) {
      setError('Enter prices on the Pricing tab before billing.');
      return;
    }
    if (!branch) {
      setError('Select a branch before billing.');
      return;
    }

    setProcessing(true);
    setError('');
    try {
      const payload = {
        patient_id: patient.patient_id,
        procedure_ids: [procedureId],
        visit_ids: [],
        lab_request_ids: [],
        radiology_request_ids: [],
        branch,
        updatedBy: user?.portal_id,
        zoho_sales_id: null,
        admission_id: null,
        customer_id: null,
        pay_through_account: payments ? null : payThroughAccount,
        split_payment: !!payments,
        payments: payments || null,
      };

      if (mergeSelected) {
        payload.zoho_sales_id = admission?.zoho_sales_id || null;
        payload.admission_id = admission?.admission_id || null;
      }

      if (
        admission &&
        patient?.patient_type !== 'Company' &&
        (invoiceChoice === 'new' || !admission.zoho_sales_id)
      ) {
        payload.customer_id = ADMISSION_PRIVATE_CUSTOMER_ID;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/billing/process-patient-bill`,
        payload
      );
      const salesNumber = response.data?.sales_number;
      alert(salesNumber ? `Invoice processed successfully: ${salesNumber}` : 'Invoice processed successfully.');
      if (typeof onBilled === 'function') {
        onBilled(response.data);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to bill surgery.');
    } finally {
      setProcessing(false);
    }
  };

  const handleBill = async () => {
    if (isPrivateWalkIn) {
      setShowPayThroughModal(true);
      return;
    }
    await submitBill();
  };

  if (showPayThroughModal) {
    return (
      <PayThroughAccountModal
        onClose={() => setShowPayThroughModal(false)}
        onConfirm={(accountNumber) => {
          setShowPayThroughModal(false);
          submitBill({ payThroughAccount: accountNumber });
        }}
        onSplitPayment={() => {
          setShowPayThroughModal(false);
          setShowSplitPaymentModal(true);
        }}
        allowSplitPayment={isPrivateWalkIn && (context?.total_agreed || 0) > 0}
        branch={branch}
        registerClosed={registerClosed}
      />
    );
  }

  if (showSplitPaymentModal) {
    return (
      <SplitPaymentModal
        onClose={() => {
          setShowSplitPaymentModal(false);
          setShowPayThroughModal(true);
        }}
        onConfirm={(payments) => {
          setShowSplitPaymentModal(false);
          submitBill({ payments });
        }}
        billTotal={context?.total_agreed || 0}
        branch={branch}
        registerClosed={registerClosed}
      />
    );
  }

  if (!loading && admission && !invoiceChoice) {
    return (
      <AdmissionInvoicePrompt
        admission={admission}
        disabled={processing}
        onMerge={() => setInvoiceChoice('merge')}
        onNew={() => setInvoiceChoice('new')}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="modal-overlay bill-surgery-overlay">
      <div className="bill-surgery-modal" role="dialog" aria-labelledby="bill-surgery-title">
        <div className="modal-header">
          <h2 id="bill-surgery-title">Bill Surgery</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <p>Loading surgery billing details...</p>
          ) : error && !context ? (
            <div className="bill-surgery-error">{error}</div>
          ) : (
            <>
              <div className="bill-surgery-summary">
                <p>
                  <strong>{context.procedures?.[0]?.procedure_name || 'Surgery'}</strong>
                </p>
                {context.priced_lines?.length > 0 ? (
                  <ul>
                    {context.priced_lines.map((line, index) => (
                      <li key={`${line.item_name}-${index}`}>
                        {line.item_name}: {formatAgreedAmount(line.agreed_amount)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No prices have been entered.</p>
                )}
                <p className="bill-surgery-total">
                  Total: {formatAgreedAmount(context.total_agreed)}
                </p>
              </div>

              {invoiceChoice === 'merge' && admission && (
                <p>
                  Charges will be added to admission invoice {admission.sales_number || '(new admission invoice)'}.
                </p>
              )}

              <div className="form-group">
                <label htmlFor="bill-surgery-branch">Branch*</label>
                <select
                  id="bill-surgery-branch"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  disabled={processing}
                >
                  <option value="">Select branch</option>
                  <option value="Pultney">Pultney</option>
                  <option value="Kissy">Kissy</option>
                </select>
              </div>

              {error && <div className="bill-surgery-error">{error}</div>}

              <div className="bill-surgery-actions">
                <button
                  type="button"
                  className="primary-btn"
                  onClick={handleBill}
                  disabled={processing || !context?.has_prices}
                >
                  {processing ? 'Processing...' : 'Process Invoice'}
                </button>
                <button type="button" className="cancel-btn" onClick={onClose} disabled={processing}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillSurgeryModal;
